import { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as analysisState from '@/components/Analysis/state';
import { useImageFromLocationId } from '@/components/Analysis/Viewer/hooks/useImageFromLocationId';
import { BLISTER_TYPE } from '@/constants';
import { AnalysisDrawerRight, Point3d, PointOfInterestTemplateDocument } from '@/types';
import { titleCase, toggleValue } from '@/utils';
import { PointOfInterest } from '@/__generated__/graphql';

export const useRemeasureProfile = (selectedPoiInfo: Partial<PointOfInterest>) => {
  const [editPointOfInterest, setEditPointOfInterest] = useAtom(poiState.editPointOfInterest);
  const [drawerRight, setDrawerRight] = useAtom(analysisState.drawerRight);
  const [selectedSpherical, setSpherical] = useAtom(analysisState.selectedSpherical);
  const setBlisterToAdd = useSetAtom(poiState.blisterToAdd);
  const setDrawerRightPreviousState = useSetAtom(analysisState.drawerRightPreviousState);
  const setCameraTarget = useSetAtom(analysisState.cameraTarget);
  const setWidgetTemplate = useSetAtom(poiState.selectedTemplate);
  const allViewpoints = useAtomValue(analysisState.structureLocations);
  const allPointOfInterestTemplates = useAtomValue(analysisState.allPointOfInterestTemplates);

  const getSphericalFromId = useImageFromLocationId(allViewpoints);

  const handleRightToggle = useCallback(
    (property: keyof AnalysisDrawerRight) => {
      setDrawerRight(toggleValue<AnalysisDrawerRight>(drawerRight, property));
      setDrawerRightPreviousState(property);
    },
    [drawerRight, setDrawerRight, setDrawerRightPreviousState]
  );

  const remeasureProfile = useCallback(() => {
    handleRightToggle('equipment');
    if (!selectedSpherical && selectedPoiInfo.location?.id) {
      const newSpherical = getSphericalFromId(selectedPoiInfo.location?.id);
      if (newSpherical?.name) {
        const { x, y, z } = selectedPoiInfo.centerPoint3d || {};
        if (!x || !y || !z) return;
        const lookAt: Point3d = { x, y, z };
        setCameraTarget({
          position: newSpherical.position,
          lookAt: [x, y, z],
        });
        setSpherical({
          ...newSpherical,
          lookAt,
        });
      }
    }
    const template = allPointOfInterestTemplates.find(
      ({ name }) => name === titleCase(BLISTER_TYPE)
    ) as PointOfInterestTemplateDocument;
    setWidgetTemplate(template);
    setBlisterToAdd({ state: 'WaitingForCenter' });
    setEditPointOfInterest({
      ...editPointOfInterest,
      state: 'WaitingForPoint',
      pointOfInterest: selectedPoiInfo,
      isAutoGenerated: true,
      poiSphericalId: selectedPoiInfo.location?.id,
      template,
    });
  }, [
    handleRightToggle,
    selectedSpherical,
    selectedPoiInfo,
    getSphericalFromId,
    setCameraTarget,
    setSpherical,
    allPointOfInterestTemplates,
    setWidgetTemplate,
    setBlisterToAdd,
    setEditPointOfInterest,
    editPointOfInterest,
  ]);

  return { remeasureProfile };
};
